import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Building2, ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { CookieBanner } from '@/features/legal/components/CookieBanner';
import type { Database } from '@/types/database.types';

type LegalKey = 'privacy' | 'terms' | 'dpo' | 'subprocessors';
type LegalDocument = Database['public']['Tables']['network_legal_documents']['Row'];
type SiteLegalDocument = Database['public']['Tables']['site_legal_documents']['Row'];

const pages: Array<{ key: LegalKey; label: string }> = [
  { key: 'privacy', label: 'Privacidade' },
  { key: 'terms', label: 'Termos' },
  { key: 'dpo', label: 'Encarregado' },
  { key: 'subprocessors', label: 'Sub-operadores' },
];

const routeKeyMap: Record<string, LegalKey> = {
  privacidade: 'privacy',
  termos: 'terms',
  encarregado: 'dpo',
  'sub-operadores': 'subprocessors',
};

function isPlatformHost(hostname: string) {
  const platformHostname = (import.meta.env.VITE_PLATFORM_HOSTNAME as string | undefined) ?? 'lumenise.app';
  return hostname === platformHostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.pages.dev');
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function LegalLayout() {
  const { slug, documentKey = 'privacidade' } = useParams<{ slug?: string; documentKey?: string }>();
  const activeKey = routeKeyMap[documentKey] ?? 'privacy';
  const hostname = window.location.hostname.toLowerCase();
  const resolveByHost = !slug && !isPlatformHost(hostname);

  const { data: hostSite } = useQuery({
    queryKey: ['legal-site-host', hostname],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('resolve_site_by_hostname', { input_hostname: hostname });
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: resolveByHost,
  });
  const effectiveSlug = slug ?? hostSite?.slug;
  const { data: site } = useQuery({
    queryKey: ['legal-site', effectiveSlug],
    queryFn: async () => {
      if (!effectiveSlug) return null;
      const { data, error } = await supabase.from('sites').select('id, name, slug, site_branding(app_name, logo_url)').eq('slug', effectiveSlug).eq('active', true).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!effectiveSlug,
  });
  const { data: networkDocument, isLoading: loadingNetwork } = useQuery<LegalDocument | null>({
    queryKey: ['public-legal-document', 'network', activeKey],
    queryFn: async () => {
      const { data, error } = await supabase.from('network_legal_documents').select('*').eq('document_key', activeKey).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const { data: override, isLoading: loadingOverride } = useQuery<SiteLegalDocument | null>({
    queryKey: ['public-legal-document', site?.id, activeKey],
    queryFn: async () => {
      if (!site?.id) return null;
      const { data, error } = await supabase.from('site_legal_documents').select('*').eq('site_id', site.id).eq('document_key', activeKey).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!site?.id,
  });

  const document = override ?? networkDocument;
  const branding = site?.site_branding as { app_name?: string; logo_url?: string | null } | null;
  const siteName = branding?.app_name || site?.name || 'Lumenise CMS';
  const rootHref = effectiveSlug && isPlatformHost(hostname) ? `/s/${effectiveSlug}` : '/';
  const legalBase = effectiveSlug && isPlatformHost(hostname) ? `/s/${effectiveSlug}/legal` : '/legal';
  const loading = loadingNetwork || (resolveByHost && !hostSite) || (!!effectiveSlug && !site) || loadingOverride;

  return (
    <div className="min-h-screen bg-surface text-ink">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4 px-6 py-4">
          <Link to={rootHref} className="flex min-w-0 items-center gap-3 font-display text-lg font-bold text-ink">
            {branding?.logo_url ? <img src={branding.logo_url} alt="" className="h-8 max-w-32 object-contain" /> : <Building2 className="h-5 w-5 text-accent" />}
            <span className="truncate">{siteName}</span>
          </Link>
          <Link to={rootHref} className="inline-flex shrink-0 items-center gap-2 text-sm font-medium text-muted hover:text-ink"><ArrowLeft className="h-4 w-4" /> Voltar ao site</Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-6 py-10 pb-36">
        <div className="mb-8 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-accent"><ShieldCheck className="h-5 w-5" /></div>
          <div><p className="text-sm font-medium text-muted">Informacoes legais</p><h1 className="font-display text-3xl font-bold text-ink">Transparencia e privacidade</h1></div>
        </div>
        <nav className="mb-6 flex flex-wrap gap-2" aria-label="Documentos legais">
          {pages.map((page) => <Link key={page.key} to={`${legalBase}/${page.key === 'privacy' ? 'privacidade' : page.key === 'subprocessors' ? 'sub-operadores' : page.key === 'dpo' ? 'encarregado' : 'termos'}`} className={`rounded-lg px-3 py-2 text-sm font-medium ${activeKey === page.key ? 'bg-primary text-white' : 'border border-border bg-card text-muted hover:text-ink'}`}>{page.label}</Link>)}
        </nav>
        <article className="rounded-xl border border-border bg-card p-6 sm:p-9">
          {loading ? <div className="flex min-h-48 items-center justify-center"><div className="h-7 w-7 animate-spin rounded-full border-2 border-accent border-t-transparent" /></div> : document ? <>
            <h2 className="font-display text-2xl font-bold text-ink">{document.title}</h2>
            <p className="mt-2 text-sm text-muted">{override ? `${siteName} - versao propria` : 'Documento padrao da rede'} · Atualizado em {formatDate(document.updated_at)}</p>
            <div className="mt-8 whitespace-pre-line text-[15px] leading-7 text-muted">{document.content}</div>
          </> : <div className="flex items-start gap-3 text-muted"><AlertCircle className="mt-0.5 h-5 w-5 text-danger" /><p>Este documento ainda nao foi publicado.</p></div>}
        </article>
      </main>
      <footer className="border-t border-border bg-card px-6 py-6 text-center text-xs text-muted">© {new Date().getFullYear()} {siteName}. Informacoes legais.</footer>
      <CookieBanner siteKey={site?.id} />
    </div>
  );
}
